import {
  buildOriginNlSourceTags,
  registrationFormFactory,
  sendSyneriseForm,
  updateSyneriseClient,
} from "apps/visits/packages/shared/modules/synerise";
import { mapBooleanToEnabledOrDisabled } from "apps/visits/packages/shared/utils/boolean";
import type { SubmitErrorHandler } from "react-hook-form";
import { useLastPageView } from "@/core/modules/tracking/use-last-page-view";
import { useCreateOrder } from "../../api/create-order.mutation";
import type { SimplifyRegisterDTO, StandardRegisterDTO } from "../../api/group-actions-ordering.types";
import { useRegister } from "../../api/register.mutation";
import type { CodeValidationFormValues, StandardRegistrationFormValues } from "../../components";
import { useCartStore } from "../../store/use-cart-store";
import { useOrderingStore } from "../../store/use-ordering-store";
import {
  safeTrackEvent,
  signUpErrorEvent,
  signUpEvent,
  signUpNewsletterEvent,
  verificationErrorEvent,
  verificationSuccessEvent,
} from "../../tracking/events";
import { formatDateOfBirthForApi } from "../../utils/format-date-of-birth-for-api";
import { writeGaUserType } from "../../utils/ga-user-type";
import { useHandleCreateOrderErrors } from "../use-handle-create-order-errors";
import { useRoutes } from "../use-routes";
import { extractRegistrationErrorType, handleRegistrationApiErrors } from "./handle-registration-errors";

export const useRegistration = () => {
  const { personalDetails, timeSlot, agreements, verificationTokenUuid, setEmail } = useOrderingStore();
  const { mutate: register, error: registerError, isLoading: isRegisterLoading } = useRegister({});
  const { products } = useCartStore();
  const { mutate: createOrder, error: createOrderError, isLoading: isCreateOrderLoading } = useCreateOrder({});
  const formattedCreateOrderError = useHandleCreateOrderErrors(createOrderError);
  const { goToHomePage, goToSummary } = useRoutes();
  const { getLastPageView, setLastPageView } = useLastPageView();
  const isLoading = isRegisterLoading || isCreateOrderLoading;

  const handleRegistration = (data: CodeValidationFormValues | StandardRegistrationFormValues) => {
    if (!personalDetails || !agreements) {
      console.error("No personal details, agreements");
      return goToHomePage();
    }
    let registrationData: StandardRegisterDTO | SimplifyRegisterDTO;

    const baseRegisterInfo = {
      dialCode: personalDetails.dialCode,
      phone: personalDetails.phone,
      firstName: personalDetails.firstName,
      lastName: personalDetails.lastName,
      pesel: personalDetails.pesel,
      dateOfBirth: formatDateOfBirthForApi(personalDetails.dateOfBirth),
      ...agreements,
    };
    const isSimplifyFlow = !!verificationTokenUuid;

    if (isSimplifyFlow) {
      registrationData = {
        ...data,
        ...baseRegisterInfo,
        verificationTokenUuid,
      } as SimplifyRegisterDTO;
    } else {
      const { emailConfirmation, ...restData } = data as StandardRegistrationFormValues;

      registrationData = {
        ...restData,
        ...baseRegisterInfo,
        ...agreements,
      } as StandardRegisterDTO;
    }

    register(registrationData, {
      onSuccess: (response) => {
        const { dateOfBirth, ...restPersonalDetails } = personalDetails;
        // REV-145: registration response carries the patient uuid (null when restricted_app_access).
        const patientUuid = response.patient?.uuid ?? null;
        // REV-211: registration response also carries visits_count; propagate it to
        // user_ordered_visits (?? 0 — non-null number; backend sends null for restricted_app_access).
        const userOrderedVisits = response.patient?.visitsCount ?? 0;

        if (!isSimplifyFlow) {
          // Standard registration → REV-109 sign_up + REV-108 sign_up_newsletter.
          // user_id is the patient uuid returned by the backend; propagate it to lastPageView
          // so the subsequent page_view also carries it.
          const lastPageView = getLastPageView();
          if (lastPageView) {
            setLastPageView({ ...lastPageView, user_id: patientUuid, user_ordered_visits: userOrderedVisits });
          }
          safeTrackEvent(() => signUpEvent({ userId: patientUuid }), "sign_up", "register.onSuccess standard");
          writeGaUserType("new_registered");
          if (agreements.acceptNewsletter) {
            safeTrackEvent(
              () => signUpNewsletterEvent(),
              "sign_up_newsletter",
              "register.onSuccess standard newsletter",
            );
          }
        } else {
          // Simplify-register flow == guest verification (REV-111). Per spec the user_id stays
          // null for guests — do not write the patient uuid into lastPageView either.
          safeTrackEvent(
            () => verificationSuccessEvent({ userId: null, method: "guest" }),
            "verification_success",
            "register.onSuccess simplify",
          );
          writeGaUserType("guest");
          if (agreements.acceptNewsletter) {
            safeTrackEvent(
              () => signUpNewsletterEvent(),
              "sign_up_newsletter",
              "register.onSuccess simplify newsletter",
            );
          }
        }

        if ("email" in data && data.email) {
          setEmail(data.email);
          try {
            void sendSyneriseForm(
              registrationFormFactory("registration_form_group_action", {
                email: data.email,
                phone: personalDetails.phone,
                firstname: personalDetails.firstName,
                lastname: personalDetails.lastName,
                newsletterAgreement: mapBooleanToEnabledOrDisabled(agreements.acceptNewsletter),
                phoneContactAgreement: mapBooleanToEnabledOrDisabled(agreements.acceptMarketingPhoneContact),
                isB2b: true,
              }),
            ).then(() => {
              // PS-1676: po identyfikacji doczepiamy origin/nlsource tylko gdy user zapisał się do newslettera.
              if (agreements.acceptNewsletter) {
                updateSyneriseClient({ tags: buildOriginNlSourceTags("b2b", "group_action") });
              }
            });
          } catch (error) {
            console.error("Error sending synerise form", error);
          }
        }

        createOrder(
          {
            bloodTestPackageUuids: products.map((product) => product.uuid),
            time: timeSlot ? timeSlot.toISOString() : undefined,
            dateOfBirth: formatDateOfBirthForApi(dateOfBirth),
            ...restPersonalDetails,
            ...agreements,
          },
          {
            onSuccess: (response) => {
              goToSummary(response.order.uuid);
            },
          },
        );
      },
      onError: (error) => {
        const errorType = extractRegistrationErrorType(error);
        if (!isSimplifyFlow) {
          safeTrackEvent(() => signUpErrorEvent({ errorType }), "sign_up_error", "register.onError standard");
        } else {
          // Guest verification failure (e.g. invalid code, code_expired).
          safeTrackEvent(
            () => verificationErrorEvent({ errorType, method: "guest" }),
            "verification_error",
            "register.onError simplify",
          );
        }
      },
    });
  };

  const handleRegistrationInvalidSubmit: SubmitErrorHandler<StandardRegistrationFormValues> = (errors) => {
    // Only the standard registration form (no verification token) reports sign_up_error here.
    if (verificationTokenUuid) return;
    // Bounded cardinality: first invalid field (sorted) or "validation". See use-phone-verification-logic.
    const errorType = Object.keys(errors).sort()[0] ?? "validation";
    safeTrackEvent(() => signUpErrorEvent({ errorType }), "sign_up_error", "registration handleSubmit invalid");
  };

  return {
    handleRegistration,
    handleRegistrationInvalidSubmit,
    isLoading,
    registerError: handleRegistrationApiErrors(registerError),
    createOrderError: formattedCreateOrderError,
  };
};
